import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Collapse, 
  IconButton, 
  Tooltip,
  Paper,
  Chip,
  Divider
} from '@mui/material';
import CodeHighlight from './CodeHighlight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import InfoIcon from '@mui/icons-material/Info';
import DescriptionIcon from '@mui/icons-material/Description';
import { getSegmentDescription, getSegmentDetailedInfo, getElementDescription } from '../utils/eancomSegmentDescriptions';

/**
 * Component to display segment data in a tree-like structure
 */
const SegmentTree = ({ 
  segment, 
  level = 0, 
  expanded = true, 
  showDetails = true,
  onToggleDetails = null,
  delimiters = null,
  highlightFirstElement = false
}) => {
  const [isExpanded, setIsExpanded] = useState(expanded);
  const [showFullDetails, setShowFullDetails] = useState(showDetails);
  const [showDocumentation, setShowDocumentation] = useState(false);

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  const handleToggleDetails = () => {
    const newState = !showFullDetails;
    setShowFullDetails(newState);
    if (onToggleDetails) {
      onToggleDetails(newState);
    }
  };

  const handleToggleDocumentation = () => {
    setShowDocumentation(!showDocumentation);
  };

  if (!segment) {
    return null;
  }

  const { id, elements, children } = segment;
  const segmentDescription = getSegmentDescription(id);
  const segmentDetailedInfo = getSegmentDetailedInfo(id);
  
  // Format indentation based on level
  const indentation = level * 16;
  
  return (
    <Box sx={{ mb: 1 }}>
      <Paper
        elevation={0}
        sx={{
          border: '1px solid #e0e0e0',
          borderLeft: `4px solid ${level === 0 ? '#2e7d32' : '#0277bd'}`,
          borderRadius: 1,
          overflow: 'hidden'
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            pl: 1,
            pr: 2,
            py: 1,
            backgroundColor: level === 0 ? 'rgba(46, 125, 50, 0.05)' : 'rgba(2, 119, 189, 0.05)',
            borderBottom: isExpanded && (elements?.length > 0 || children?.length > 0) ? '1px dashed rgba(0, 0, 0, 0.1)' : 'none',
            cursor: 'pointer'
          }}
          onClick={handleToggle}
        >
          {/* Expand/collapse button */}
          {(elements?.length > 0 || children?.length > 0) && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleToggle();
              }}
              sx={{ mr: 1 }}
              aria-label={isExpanded ? "Collapse segment" : "Expand segment"}
            >
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          )}
          
          {/* Segment ID and description */}
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
              <CodeHighlight 
                code={id} 
                type="segment"
                fontSize="0.95rem"
              />
              
              <Typography 
                variant="body2" 
                sx={{ 
                  ml: 1.5,
                  color: 'text.secondary',
                  fontWeight: 400
                }}
              >
                {segmentDescription}
              </Typography>
            </Box>
          </Box>
          
          {/* Documentation button */}
          {segmentDetailedInfo && (
            <Tooltip title="Show documentation">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggleDocumentation();
                }}
                sx={{ mr: 1 }}
                aria-label="Show segment documentation"
              >
                <DescriptionIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          
          {/* Toggle details button */}
          {(elements?.length > 0 || children?.length > 0) && (
            <Tooltip title={showFullDetails ? "Hide details" : "Show details"}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggleDetails();
                }}
                aria-label={showFullDetails ? "Hide details" : "Show details"}
              >
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        
        {/* Documentation section */}
        {segmentDetailedInfo && (
          <Collapse in={showDocumentation && isExpanded}>
            <Box sx={{ 
              p: 2, 
              backgroundColor: 'rgba(0, 0, 0, 0.02)',
              borderBottom: '1px solid #e0e0e0'
            }}>
              <Typography variant="subtitle2" gutterBottom>
                <InfoIcon 
                  fontSize="small" 
                  sx={{ 
                    verticalAlign: 'middle', 
                    mr: 1, 
                    color: 'primary.main' 
                  }} 
                />
                {segmentDetailedInfo.title} - Documentation
              </Typography>
              
              <Typography variant="body2" gutterBottom sx={{ mb: 2 }}>
                <strong>Function:</strong> {segmentDetailedInfo.function}
              </Typography>
              
              {segmentDetailedInfo.notes && (
                <Typography variant="body2" sx={{ fontStyle: 'italic', mb: 2 }}>
                  <strong>Note:</strong> {segmentDetailedInfo.notes}
                </Typography>
              )}

              {segmentDetailedInfo.elements && (
                <>
                  <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                    Element Structure:
                  </Typography>
                  
                  <Box sx={{ mb: 2 }}>
                    {segmentDetailedInfo.elements.map((element, idx) => (
                      <Box 
                        key={idx} 
                        sx={{ 
                          mb: 1, 
                          display: 'flex',
                          alignItems: 'flex-start'
                        }}
                      >
                        <Chip
                          label={element.id}
                          size="small"
                          variant="outlined"
                          color="primary"
                          sx={{ mr: 1, minWidth: 60, fontSize: '0.7rem' }}
                        />
                        <Box>
                          <Typography variant="body2" component="div" sx={{ fontWeight: 500 }}>
                            {element.name}
                            {element.mandatory && (
                              <Chip
                                label="M"
                                size="small"
                                color="secondary"
                                sx={{ ml: 1, height: 16, fontSize: '0.6rem' }}
                              />
                            )}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {element.description}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </>
              )}
              
              {/* Qualifier Values if they exist */}
              {segmentDetailedInfo.qualifiers && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                    Common Qualifier Codes:
                  </Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    gap: 1,
                    maxHeight: '200px',
                    overflowY: 'auto',
                    pr: 1
                  }}>
                    {Object.entries(segmentDetailedInfo.qualifiers).map(([code, meaning]) => (
                      <Chip
                        key={code}
                        label={`${code}: ${meaning}`}
                        size="small"
                        variant="outlined"
                        sx={{ 
                          fontSize: '0.7rem',
                          bgcolor: 'rgba(255, 255, 255, 0.7)' 
                        }}
                      />
                    ))}
                  </Box>
                </>
              )}
            </Box>
          </Collapse>
        )}
        
        {/* Content */}
        <Collapse in={isExpanded}>
          <Box sx={{ p: 2 }}>
            {/* Special UNA explainer embedded in the segment */}
            {segment.id === 'UNA' && (
              <Box sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                mb: 2
              }}>
                <Chip label={`Component separator: ${delimiters?.componentDataSeparator || ':'}`} size="small" variant="outlined" />
                <Chip label={`Element separator: ${delimiters?.dataElementSeparator || '+'}`} size="small" variant="outlined" />
                <Chip label={`Decimal notation: ${delimiters?.decimalNotation || '.'}`} size="small" variant="outlined" />
                <Chip label={`Release character: ${delimiters?.releaseCharacter || '?'}`} size="small" variant="outlined" />
                <Chip label={`Repetition separator: ${delimiters?.repetitionSeparator || '*'}`} size="small" variant="outlined" />
                <Chip label={`Segment terminator: ${delimiters?.segmentTerminator || "'"}`} size="small" variant="outlined" />
                <Divider sx={{ width: '100%' }} />
              </Box>
            )}
            {/* Elements */}
            {elements && elements.length > 0 && (
              <Box sx={{ ml: indentation / 2 }}>
                {elements.map((element, index) => {
                  const elementInfo = getElementDescription(id, index + 1);
                  
                  // Debug for BGM segment
                  if (id === 'BGM') {
                    console.log(`BGM element ${index + 1}:`, element, "Element info:", elementInfo);
                  }
                  
                  return (
                    <Box 
                      key={index}
                      sx={{ 
                        mb: 1.5,
                        display: 'flex',
                        alignItems: 'flex-start',
                        backgroundColor: highlightFirstElement && index === 0 ? 'rgba(255, 152, 0, 0.12)' : 'transparent',
                        borderRadius: highlightFirstElement && index === 0 ? 1 : 0,
                        px: highlightFirstElement && index === 0 ? 0.5 : 0
                      }}
                    >
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          minWidth: 24, 
                          color: 'text.secondary',
                          mt: 0.5
                        }}
                      >
                        {index + 1}.
                      </Typography>
                      
                      <Box sx={{ flex: 1 }}>
                        {Array.isArray(element) ? (
                          // Handle composite elements
                          <Box sx={{ pl: 1 }}>
                            {element.map((comp, compIndex) => {
                              // Check for qualifier values
                              let tooltip = null;
                              if (compIndex === 0 && elementInfo?.values && elementInfo.values[comp]) {
                                tooltip = elementInfo.values[comp];
                              }
                              
                              // Also check explanations directly for qualifier codes
                              if (compIndex === 0 && !tooltip && elementInfo?.explanations && elementInfo.explanations[comp]) {
                                tooltip = elementInfo.explanations[comp];
                              }
                              
                              // Special handling for UNB syntax identifier (UNOC:3)
                              if (id === 'UNB' && index === 0 && compIndex === 0 && comp.includes(':')) {
                                const syntaxId = comp.split(':')[0];
                                if (elementInfo?.explanations && elementInfo.explanations[syntaxId]) {
                                  tooltip = elementInfo.explanations[syntaxId];
                                }
                              }
                              
                              // Special handling for UNH message identifier components
                              if (id === 'UNH' && index === 1) {
                                // Process message type, version, release, and controlling agency
                                if (compIndex === 0 && elementInfo?.subElements?.[0]?.explanations) {
                                  // Message type (INVOIC, ORDERS, etc.)
                                  tooltip = elementInfo.subElements[0].explanations[comp];
                                } else if (compIndex === 1 && elementInfo?.subElements?.[1]?.explanations) {
                                  // Version (D, S)
                                  tooltip = elementInfo.subElements[1].explanations[comp];
                                } else if (compIndex === 2 && elementInfo?.subElements?.[2]?.explanations) {
                                  // Release (96A, 01B, etc.)
                                  tooltip = elementInfo.subElements[2].explanations[comp];
                                } else if (compIndex === 3 && elementInfo?.subElements?.[3]?.explanations) {
                                  // Controlling agency (UN, EAN, etc.)
                                  tooltip = elementInfo.subElements[3].explanations[comp];
                                }
                              }
                              
                              return (
                                <Box 
                                  key={compIndex}
                                  sx={{ 
                                    mb: 0.75,
                                    display: 'flex',
                                    alignItems: 'flex-start'
                                  }}
                                >
                                  <Typography 
                                    variant="body2" 
                                    sx={{ 
                                      minWidth: 20, 
                                      color: 'text.secondary',
                                      fontSize: '0.75rem',
                                      mt: 0.5
                                    }}
                                  >
                                    {compIndex + 1}.
                                  </Typography>
                                  
                                  <Box>
                                    <CodeHighlight 
                                      code={comp} 
                                      type={compIndex === 0 ? 'qualifier' : 'value'}
                                      tooltip={tooltip}
                                    />
                                    
                                    {showFullDetails && (
                                      <>
                                        {compIndex === 0 && (
                                          <Typography 
                                            variant="body2" 
                                            sx={{ 
                                              mt: 0.5, 
                                              ml: 1, 
                                              color: 'text.secondary',
                                              fontSize: '0.8rem'
                                            }}
                                          >
                                            {elementInfo?.name || 'Qualifier'}
                                          </Typography>
                                        )}
                                        
                                        {tooltip && (
                                          <Typography 
                                            variant="body2" 
                                            sx={{ 
                                              mt: 0.5, 
                                              ml: 1, 
                                              color: 'text.secondary',
                                              fontSize: '0.8rem',
                                              fontStyle: 'italic'
                                            }}
                                          >
                                            {tooltip}
                                          </Typography>
                                        )}

                                        {/* Add checking for explanations and display if available */}
                                        {elementInfo?.explanations && elementInfo.explanations[comp] && (
                                          <Typography 
                                            variant="body2" 
                                            sx={{ 
                                              mt: 0.5, 
                                              ml: 1, 
                                              color: 'text.secondary',
                                              fontSize: '0.8rem',
                                              fontStyle: 'italic'
                                            }}
                                          >
                                            {elementInfo.explanations[comp]}
                                          </Typography>
                                        )}
                                      </>
                                    )}
                                  </Box>
                                </Box>
                              );
                            })}
                          </Box>
                        ) : (
                          // Handle simple elements
                          <Box>
                            <CodeHighlight 
                              code={element} 
                              type={
                                // Check for qualifiers in the first element of common segments
                                (index === 0 && 
                                 (id === 'BGM' || id === 'DTM' || id === 'NAD' || id === 'QTY' || 
                                  id === 'MOA' || id === 'RFF' || id === 'LOC' || id === 'CTA' || id === 'FII' ||
                                  id === 'PRI' || id === 'ALC' || id === 'PAT' || id === 'PYT' || id === 'TAX' ||
                                  id === 'CUX' || id === 'PIA' || id === 'IMD' || id === 'COM' || id === 'CNT' ||
                                  id === 'UNS' || id === 'UNB' || id === 'UNH')) ? 
                                    'qualifier' : 'element'
                              }
                              tooltip={
                                  // Enhanced tooltip logic for qualifiers
                                  (() => {
                                    // First check element-specific explanations/values
                                    if (elementInfo?.explanations && elementInfo.explanations[element]) {
                                      return elementInfo.explanations[element];
                                    }
                                    if (elementInfo?.values && elementInfo.values[element]) {
                                      return elementInfo.values[element];
                                    }
                                    
                                    // Then check segment-level qualifiers (for DTM, RFF, NAD, etc.)
                                    const segmentInfo = getSegmentDetailedInfo(id);
                                    if (segmentInfo?.qualifiers && segmentInfo.qualifiers[element]) {
                                      return segmentInfo.qualifiers[element];
                                    }
                                    
                                    // Special handling for NAD responsible agency codes
                                    if (id === 'NAD') {
                                      // Handle composite elements (arrays) - check for agency codes in sub-elements
                                      if (Array.isArray(element)) {
                                        for (let subElement of element) {
                                          if (subElement === '91') return 'Assigned by seller or seller\'s agent';
                                          if (subElement === '92') return 'Assigned by buyer or buyer\'s agent';
                                          if (subElement === '9') return 'EAN (EAN International)';
                                          if (subElement === '14') return 'EAN Location Code (GLN)';
                                        }
                                      }
                                      
                                      // Handle simple elements
                                      if (element === '91') return 'Assigned by seller or seller\'s agent';
                                      if (element === '92') return 'Assigned by buyer or buyer\'s agent';
                                      if (element === '9') return 'EAN (EAN International)';
                                      if (element === '14') return 'EAN Location Code (GLN)';
                                    }
                                    
                                    return null;
                                  })()
                              }
                            />
                            {showFullDetails && elementInfo && (
                              <>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    mt: 0.5, 
                                    ml: 1, 
                                    color: 'text.secondary',
                                    fontSize: '0.8rem'
                                  }}
                                >
                                  {elementInfo.name}
                                </Typography>
                                
                                {/* Check for explanation for non-array elements too */}
                                {elementInfo.explanations && elementInfo.explanations[element] && (
                                  <Typography 
                                    variant="body2" 
                                    sx={{ 
                                      mt: 0.5, 
                                      ml: 1, 
                                      color: 'text.secondary',
                                      fontSize: '0.8rem',
                                      fontStyle: 'italic'
                                    }}
                                  >
                                    {elementInfo.explanations[element]}
                                  </Typography>
                                )}

                                {/* Special handling for BGM segment values to always check qualifierCodesExplanations */}
                                {id === 'BGM' && index === 0 && !elementInfo.explanations?.[element] && elementInfo.values && (
                                  <Typography 
                                    variant="body2" 
                                    sx={{ 
                                      mt: 0.5, 
                                      ml: 1, 
                                      color: 'text.secondary',
                                      fontSize: '0.8rem',
                                      fontStyle: 'italic'
                                    }}
                                  >
                                    {elementInfo.values[element] || "Document type code"}
                                  </Typography>
                                )}
                              </>
                            )}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
            
            {/* Child segments */}
            {children && children.length > 0 && (
              <Box sx={{ mt: elements?.length > 0 ? 2 : 0 }}>
                {children.map((child, index) => (
                  <SegmentTree
                    key={index}
                    segment={child}
                    level={level + 1}
                    expanded={isExpanded && level === 0}
                    showDetails={showFullDetails}
                    onToggleDetails={onToggleDetails}
                  />
                ))}
              </Box>
            )}
          </Box>
        </Collapse>
      </Paper>
    </Box>
  );
};

export default SegmentTree; 